import { memo } from "react";
import { Box, Stack, Typography, useTheme } from "@mui/material";
import { useTranslation } from "react-i18next";
import { Button } from "@vilocnv/allsetra-core";
import { DownloadGreen, XARReportIcon } from "assets/icons";
import { TripBackupReminderPage } from "./useTripBackupReminder";

interface TripBackupReminderModalContentProps {
  page: TripBackupReminderPage;
  showXarPage: boolean;
  showXarCta: boolean;
  isAdmin: boolean;
  onGoToXarReports: () => void;
}

export const TripBackupReminderModalContent = memo(
  ({
    page,
    showXarPage,
    showXarCta,
    isAdmin,
    onGoToXarReports
  }: TripBackupReminderModalContentProps) => {
    const theme = useTheme();
    const { t } = useTranslation();
    const totalPages = showXarPage ? 2 : 1;
    const isXarPage = showXarPage && page === 2;

    return (
      <Stack direction="column" spacing={3}>
        {isXarPage ? (
          <XARReportIcon
            style={{ alignSelf: "center", width: "64px", height: "64px" }}
          />
        ) : (
          <DownloadGreen
            style={{ alignSelf: "center", width: "64px", height: "64px" }}
          />
        )}
        <Typography variant="h6" alignSelf="center">
          {isXarPage
            ? t("tripBackupReminder.xarTitle")
            : t("tripBackupReminder.title")}
        </Typography>
        <Typography variant="body1">
          {isXarPage
            ? t("tripBackupReminder.xarDescription")
            : t("tripBackupReminder.description")}
        </Typography>
        {isXarPage && (
          <Typography variant="body1">
            {isAdmin
              ? t("tripBackupReminder.xarProcedureAdmin")
              : t("tripBackupReminder.xarProcedureDriver")}
          </Typography>
        )}
        {isXarPage && showXarCta && (
          <Button
            variant="outlined"
            onClick={onGoToXarReports}
            theme={theme}
            sx={{ alignSelf: "center" }}
          >
            {t("tripBackupReminder.xarCta")}
          </Button>
        )}
        {totalPages > 1 && (
          <Stack
            direction="row"
            spacing={1}
            justifyContent="center"
            aria-label="pagination"
          >
            {Array.from({ length: totalPages }, (_, index) => {
              const pageNumber = (index + 1) as TripBackupReminderPage;
              const isActive = page === pageNumber;

              return (
                <Box
                  key={pageNumber}
                  sx={{
                    width: 8,
                    height: 8,
                    borderRadius: "50%",
                    bgcolor: isActive ? "primary.main" : "grey.400"
                  }}
                />
              );
            })}
          </Stack>
        )}
      </Stack>
    );
  }
);

TripBackupReminderModalContent.displayName = "TripBackupReminderModalContent";
